import { useNavigate, useSearch } from '@tanstack/react-router';
import JsonView from '@uiw/react-json-view';
import { diffLines } from 'diff';
import { useMemo, type CSSProperties } from 'react';
import Select, { type StylesConfig } from 'react-select';

import { useTheme } from '@/hooks/useTheme';
import type { ThemeMode } from '@/lib/theme';

import { AgentConfigEvalPanel } from './AgentConfigEvalPanel';
import { useAgentConfigNames, useAgentConfigPreview } from './useAgentConfigs';

import type { AgentConfigPreview } from './agent-config.types';

type AgentOption = { value: string; label: string };

export default function AgentConfigsPage() {
  const { data, isLoading, isError, error } = useAgentConfigNames();
  const search = useSearch({ from: '/agent-configs' });
  const navigate = useNavigate({ from: '/agent-configs' });
  const theme = useTheme();
  const selectStyles = useMemo(() => buildSelectStyles(theme), [theme]);
  const jsonStyle = useMemo(() => buildJsonStyle(theme), [theme]);

  const options = useMemo<AgentOption[]>(
    () => (data?.agents ?? []).map((name) => ({ value: name, label: name })),
    [data]
  );

  const baseName = search.base || data?.default_agent || '';
  const compareName = search.compare;
  const baseQuery = useAgentConfigPreview(baseName || undefined);
  const compareQuery = useAgentConfigPreview(compareName || undefined);

  const setBase = (name: string) => {
    navigate({
      search: { ...search, base: name },
      replace: true,
    });
  };
  const setCompare = (name: string) => {
    navigate({
      search: { ...search, compare: name },
      replace: true,
    });
  };

  if (isLoading) {
    return <div className="text-muted-foreground">Loading agent configs…</div>;
  }
  if (isError) {
    return (
      <div className="text-destructive">
        {error instanceof Error
          ? error.message
          : 'Failed to load agent configs'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">Agent configs</h2>
        <p className="text-sm text-muted-foreground">
          Resolved config map plus the static system-prompt skeleton.
          Placeholders stay unevaluated. The right column diffs its prompt
          against the left.
        </p>
      </div>
      <div className="grid h-[calc(100dvh-28rem)] grid-cols-1 gap-4 lg:grid-cols-2">
        <AgentColumn
          title="Base"
          options={options}
          selectedName={baseName}
          onSelect={setBase}
          agent={baseQuery.data}
          isAgentLoading={baseQuery.isLoading}
          selectStyles={selectStyles}
          jsonStyle={jsonStyle}
          isClearable={false}
        />
        <AgentColumn
          title="Compare"
          options={options.filter((option) => option.value !== baseName)}
          selectedName={compareName}
          onSelect={setCompare}
          agent={compareQuery.data}
          isAgentLoading={compareQuery.isLoading}
          selectStyles={selectStyles}
          jsonStyle={jsonStyle}
          isClearable
          promptDiffBase={baseQuery.data?.prompt_skeleton ?? null}
        />
      </div>
      <AgentConfigEvalPanel
        baseAgent={baseName}
        compareAgent={compareName || undefined}
        jsonStyle={jsonStyle}
      />
    </div>
  );
}

function AgentColumn({
  title,
  options,
  selectedName,
  onSelect,
  agent,
  isAgentLoading,
  selectStyles,
  jsonStyle,
  isClearable,
  promptDiffBase,
}: {
  title: string;
  options: AgentOption[];
  selectedName: string;
  onSelect: (name: string) => void;
  agent: AgentConfigPreview | undefined;
  isAgentLoading: boolean;
  selectStyles: StylesConfig<AgentOption, false>;
  jsonStyle: CSSProperties;
  isClearable: boolean;
  promptDiffBase?: string | null;
}) {
  const selected =
    options.find((option) => option.value === selectedName) ?? null;
  const showDiff = promptDiffBase != null && agent != null;

  return (
    <section className="flex h-full min-h-0 flex-col gap-3 rounded-lg border bg-card p-4">
      <div className="shrink-0 space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </div>
        <Select<AgentOption, false>
          options={options}
          value={selected}
          onChange={(option) => onSelect(option?.value ?? '')}
          isClearable={isClearable}
          isSearchable
          placeholder={isClearable ? 'Optional compare agent…' : 'Select agent'}
          styles={selectStyles}
        />
      </div>
      {agent ? (
        <div className="grid min-h-0 flex-1 grid-rows-[1fr_4fr] gap-3">
          <div className="min-h-0 overflow-auto rounded-md border bg-background p-3">
            {agent.prompt_func_label && (
              <div className="mb-2 text-xs text-muted-foreground">
                Prompt: {agent.prompt_func_label}
              </div>
            )}
            <JsonView
              value={agent.config}
              collapsed={1}
              displayDataTypes={false}
              displayObjectSize={false}
              style={jsonStyle}
            />
          </div>
          <PromptPanel
            skeleton={agent.prompt_skeleton}
            diffBase={showDiff ? promptDiffBase : null}
          />
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          {isAgentLoading
            ? 'Loading agent…'
            : isClearable
              ? 'Pick a second agent to compare config and prompt.'
              : 'Select an agent.'}
        </div>
      )}
    </section>
  );
}

function PromptPanel({
  skeleton,
  diffBase,
}: {
  skeleton: string | null;
  diffBase: string | null | undefined;
}) {
  if (skeleton == null) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
        Could not extract a static template for this prompt func.
      </div>
    );
  }

  if (diffBase != null) {
    return (
      <pre className="h-full min-h-0 overflow-auto whitespace-pre-wrap break-words rounded-md border bg-background font-mono text-xs leading-relaxed">
        {diffLines(diffBase, skeleton).flatMap((change, changeIndex) => {
          const kind = change.added
            ? 'add'
            : change.removed
              ? 'remove'
              : 'equal';
          // Chunks end with a newline, which would otherwise become a blank row.
          const lines = change.value.replace(/\n$/, '').split('\n');
          return lines.map((text, lineIndex) => (
            <DiffRow
              key={`${kind}-${changeIndex}-${lineIndex}`}
              kind={kind}
              text={text}
            />
          ));
        })}
      </pre>
    );
  }

  return (
    <pre className="h-full min-h-0 overflow-auto whitespace-pre-wrap break-words rounded-md border bg-background p-3 font-mono text-xs leading-relaxed">
      <PromptText text={skeleton} />
    </pre>
  );
}

const PLACEHOLDER_PATTERN = /(\{[a-zA-Z0-9_.]+\})/g;

/** Renders prompt text with unevaluated {placeholder} tokens highlighted. */
function PromptText({ text }: { text: string }) {
  // split with a capturing group interleaves plain text (even) and matches (odd)
  const parts = text.split(PLACEHOLDER_PATTERN);
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <span
            key={index}
            className="rounded bg-amber-500/15 font-semibold text-amber-700 dark:text-amber-300"
          >
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
}

function DiffRow({
  kind,
  text,
}: {
  kind: 'add' | 'remove' | 'equal';
  text: string;
}) {
  const prefix = kind === 'add' ? '+' : kind === 'remove' ? '-' : ' ';
  const className =
    kind === 'add'
      ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
      : kind === 'remove'
        ? 'bg-red-500/15 text-red-800 dark:text-red-300'
        : 'text-foreground/80';
  return (
    <div className={className}>
      {prefix}
      <PromptText text={text} />
    </div>
  );
}

function buildJsonStyle(theme: ThemeMode): CSSProperties {
  const isDark = theme === 'dark';
  return {
    '--w-rjv-background-color': 'transparent',
    '--w-rjv-font-family':
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    '--w-rjv-font-size': '0.75rem',
    '--w-rjv-key-string': isDark ? '#93c5fd' : '#2563eb',
    '--w-rjv-color': isDark ? '#bfdbfe' : '#1e40af',
    '--w-rjv-type-string-color': isDark ? '#6ee7b7' : '#059669',
    '--w-rjv-type-int-color': isDark ? '#c4b5fd' : '#7c3aed',
    '--w-rjv-type-boolean-color': isDark ? '#fca5a5' : '#dc2626',
    '--w-rjv-type-null-color': isDark ? '#9ca3af' : '#6b7280',
  } as CSSProperties;
}

function buildSelectStyles(theme: ThemeMode): StylesConfig<AgentOption, false> {
  const isDark = theme === 'dark';
  return {
    control: (base) => ({
      ...base,
      minHeight: '36px',
      fontSize: '14px',
      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
      borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
      boxShadow: 'none',
    }),
    menu: (base) => ({
      ...base,
      zIndex: 50,
      backgroundColor: isDark ? '#1e2535' : '#ffffff',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '13px',
      backgroundColor: state.isFocused
        ? isDark
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0,0,0,0.05)'
        : 'transparent',
      color: isDark ? '#e5e7eb' : '#111827',
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#e5e7eb' : '#111827',
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#e5e7eb' : '#111827',
    }),
  };
}
